import React from 'react'
import { useTranslation } from 'react-i18next'
import ROUTES from 'config/routes'
import { useFormContext, useWatch } from 'react-hook-form'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormField from 'components/p5/FormField'
import FormSelect from 'components/p5/FormSelect'
import Input from 'components/p5/Input'
import useNctsProfileOptions from 'features/declaration/customer-profile/components/useNctsProfileOptions'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'
import GuaranteeCalculationModal from 'features/declaration/transit-operation/components/guarantee-calculation/GuaranteeCalculationModal'
import useGuaranteeCalculationModal from 'features/declaration/transit-operation/components/guarantee-calculation/useGuaranteeCalculationModal'
import { useDeclarationEditorContext } from 'features/declaration/form/contexts/DeclarationEditorContext'

function Guarantees() {
  const { t } = useTranslation()

  const { setValue } = useFormContext<TransitOperationFormValues>()
  const { declarationData, editorData } = useDeclarationEditorContext()
  const transitOperationId = declarationData.transitOperation?.id ?? null
  const country = editorData?.country ?? null

  const customerId = useWatch<TransitOperationFormValues, 'customerId'>({ name: 'customerId' })
  const profilesAsOptions = useNctsProfileOptions(customerId, transitOperationId !== null)

  const guaranteeCalculationModal = useGuaranteeCalculationModal()
  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', [
    'guarantee',
    'guarantee.nctsProfileId',
    'guarantee.amount',
  ])

  const setGuaranteeAmount = (value: number) => {
    setValue('guarantee.amount', value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-guarantees"
        headingLabel={t('declaration.p5.guarantees')}
        fieldRule={fieldRequirements.guarantee}
      >
        <FormField
          labelKey="declaration.nctsProfile"
          fieldRule={fieldRequirements['guarantee.nctsProfileId']}
        >
          <FormSelect
            <TransitOperationFormValues>
            field="guarantee.nctsProfileId"
            labelKey="common.chooseProfile"
            options={profilesAsOptions}
            type="sync"
            fieldRule={fieldRequirements['guarantee.nctsProfileId']}
            emptyValue={null}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.amountToBeCovered"
          fieldRule={fieldRequirements['guarantee.amount']}
        >
          <div className="input-group">
            <Input
              <TransitOperationFormValues>
              field="guarantee.amount"
              labelKey="declaration.p5.amountToBeCovered"
              min="0"
              step={0.01}
              type="number"
              placeholder={t('declaration.p5.amountToBeCovered')}
              fieldRule={fieldRequirements['guarantee.amount']}
            />
            <div className="input-group-append">
              <span className="input-group-text">
                {t('goodsItem.goodsItemPrice', { context: 'unit' })}
              </span>
            </div>
          </div>
        </FormField>
        <FormField labelKey="">
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-light btn-outline-gray ms-1 mb-1 text-black-50"
              onClick={() => guaranteeCalculationModal.open(transitOperationId)}
            >
              <span>{t('guaranteeCalculator.pdf')}</span>
            </button>
          </div>
          <div className="d-flex justify-content-end">
            {
              country === 'EE' && (
                <a
                  href={ROUTES.external.eeGuarantee}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="text-black-50  pointer mb-2"
                >
                  <small>{t('declaration.eeGuarantee')}</small>
                </a>
              )
            }
            {
              country === 'LV' && (
                <a
                  href={ROUTES.external.lvGuarantee}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="text-black-50 pointer mb-2"
                >
                  <small>{t('declaration.lvGuarantee')}</small>
                </a>
              )
            }
            {
              country === 'LT' && (
                <a
                  href={ROUTES.external.ltGuarantee}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="text-black-50 pointer mb-2"
                >
                  <small>{t('declaration.ltGuarantee')}</small>
                </a>
              )
            }
            {
              country === 'PL' && (
                <a
                  href={ROUTES.external.plGuarantee}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="text-black-50 pointer mb-2"
                >
                  <small>{t('declaration.plGuarantee')}</small>
                </a>
              )
            }
          </div>
        </FormField>
      </FormCollapsibleColumn>
      {guaranteeCalculationModal.isOpen && (
        <GuaranteeCalculationModal
          transitOperationId={transitOperationId}
          country={country}
          isOpen={guaranteeCalculationModal.isOpen}
          onClose={guaranteeCalculationModal.close}
          onResolve={setGuaranteeAmount}
        />
      )}
    </section>
  )
}

export default Guarantees
